import { useAtomValue } from "jotai";
import type { PageProps as NextPageProps } from "next";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { isString } from "scmp-sdk";

import { notEmpty, parseQueryString } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PlusHomeSeo } from "scmp-app/components/plus/plus-home-seo";
import { sanitizeKeyword } from "scmp-app/components/search/helpers";
import { useSearchResultStreamSubscription } from "scmp-app/components/search/hooks";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { useGetPageLocationForPlusPage } from "scmp-app/pages/plus/hooks";
import { plusGlobalCss } from "scmp-app/pages/plus/styles";
import type { searchPlusSearchPageQuery } from "scmp-app/queries/__generated__/searchPlusSearchPageQuery.graphql";

import { StyledSearch } from "./styles";

const query = graphql`
  query searchPlusSearchPageQuery($nodeId: ID!, $hasNodeId: Boolean!) {
    node(id: $nodeId) @include(if: $hasNodeId) {
      ...plusHomeSeoNode
    }
  }
`;

type PageProps = NextPageProps;

const SearchPage: RelayComponent<PageProps, searchPlusSearchPageQuery> = ({ preloadedQuery }) => {
  const queryReference = usePreloadedQuery(query, preloadedQuery);

  const { query: routerQuery } = useRouter();
  const routeQueryValue = parseQueryString(routerQuery.q) ?? "";
  const cleanRouteQueryValue = sanitizeKeyword(routeQueryValue);
  const { streamId } = useSearchResultStreamSubscription();

  const pageLocation = useGetPageLocationForPlusPage();
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    if (!notEmpty(pageLocation)) return;
    trackPageView?.({
      page_location: pageLocation,
    });
  }, [pageLocation, trackPageView]);

  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  return (
    <>
      <PlusHomeSeo reference={queryReference.node} title="Search" />
      <StyledSearch
        $stickyStackedHeight={appBarStickyHeight}
        routeQueryValue={cleanRouteQueryValue}
        searchPageType="plus"
        streamId={streamId}
      />
    </>
  );
};

const pageProps: PageProps = {
  animateKey: "plus-search",
  appBarConfiguration: {
    variant: "scmp/plus",
  },
  appInitializerConfiguration: {
    application: "plus",
  },
  extraGlobalCss: plusGlobalCss,
};

export default withRelay(SearchPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    return {
      hasNodeId: notEmpty(context.query?.nodeId),
      nodeId: isString(context.query.nodeId) ? (context.query?.nodeId ?? "") : "",
    };
  },
});
